/* eslint-disable no-restricted-globals */
import i18next from 'i18next';
import {
  transactionCaptionTypes,
  transactionFormatTypes,
  transactionTypes,
  transactionTypesArray,
  transactionUnitTypesArray,
} from '../../enum/api/transactionTypes';
import { formatDate } from '../dateHelpers';
import { parseEnumType } from '../enumMappers';
import { formatMoneyNumeral } from '../numeralHelpers';

// An executed Target Price Order is an ordinary Buy for Storage that the BE flags
// (IsTriggerOrder), and it reads as the Target Price Order does ("Buy at Target
// Price for Storage 3 units"): this is its summary Field with the type changed to
// TriggerOrderBuy, whether the Field's type is the BE's number (a list row) or
// its name (a transaction's details). The summary of any other type, a
// reservation or a release, is returned as it is.
export const asTargetPriceOrderSummary = (field) => {
  const transactionType =
    typeof field.TransactionType === 'number'
      ? parseEnumType(transactionTypesArray, field.TransactionType)
      : field.TransactionType;

  return transactionType === transactionTypes.BuyforStorage
    ? { ...field, TransactionType: transactionTypes.TriggerOrderBuy }
    : field;
};

// isRow: the text is for a list row, which can word a type differently
// (portfolioRowTypes) from the same transaction's details.
export const formatByTransactionSummary = (field, { isRow = false } = {}) => {
  const amountOrQuantity = field.AmountOrQuantity;
  const precision = field.Precision;
  const unitType =
    typeof field.UnitType === 'number'
      ? parseEnumType(transactionUnitTypesArray, field.UnitType)
      : field.UnitType;

  const decimalAmount =
    amountOrQuantity > 1
      ? `${parseFloat(amountOrQuantity).toFixed(precision)} ${i18next.t(
          `portfolio.transactions.details.pluralUnit`,
        )}`
      : `${parseFloat(amountOrQuantity).toFixed(precision)} ${i18next.t(
          `portfolio.transactions.details.singleUnit`,
        )}`;

  const amount =
    unitType === 'Cash' ? formatMoneyNumeral(amountOrQuantity) : decimalAmount;

  const transactionTypeLiteral =
    typeof field.TransactionType === 'number'
      ? parseEnumType(transactionTypesArray, field.TransactionType)
      : field.TransactionType;

  const rowTypeKey = `portfolio.transactions.portfolioRowTypes.${transactionTypeLiteral}`;
  const typeKey =
    isRow && i18next.exists(rowTypeKey)
      ? rowTypeKey
      : `portfolio.transactions.portfolioTypes.${transactionTypeLiteral}`;

  const transactionType = amountOrQuantity
    ? i18next.t(typeKey, { amount })
    : i18next.t(
        `portfolio.transactions.portfolioTypesNonAmount.${transactionTypeLiteral}`,
      );

  return transactionType;
};

// Whether a list row is a Buy at Target Price: the order itself (type 43) or the
// buy it placed, which the BE flags (IsTriggerOrder).
export const isTargetPriceBuyTransaction = ({ Type, IsTriggerOrder }) =>
  !!IsTriggerOrder ||
  parseEnumType(transactionTypesArray, Type) ===
    transactionTypes.TriggerOrderBuy;

const countDecimals = (value) => (String(value).split('.')[1] || '').length;

// The summary of a Buy at Target Price list row, which reads as the details' do
// ("Buy at Target Price for Storage 3 units"). Its quantity is its Summary
// Field's or, for a row that carries none (the product page's), its own Quantity.
export const getTargetPriceBuyRowSummary = ({ Fields, Quantity }) => {
  const summary = Fields?.[0];
  const quantity = summary?.AmountOrQuantity ?? Quantity;

  return formatByTransactionSummary(
    {
      ...summary,
      TransactionType: transactionTypes.TriggerOrderBuy,
      AmountOrQuantity: quantity,
      Precision: summary?.Precision ?? countDecimals(quantity),
    },
    { isRow: true },
  );
};

export const formatByDateRange = (field) => {
  const formatedStartDate = formatDate(field.StartDate);
  const formatedEndDate = formatDate(field.EndDate);

  return `(${formatedStartDate} ${i18next.t('common.to')} ${formatedEndDate})`;
};

const formatByProduct = (field) => {
  const product = field.ProductCaption;

  return product;
};

export const getInsiderSavingsSubtext = (fields) => {
  const discountField = fields.find((field) => {
    const captionType =
      typeof field.CaptionType === 'number'
        ? parseEnumType(transactionCaptionTypes, field.CaptionType)
        : field.CaptionType;
    return captionType === 'InsiderStorageCreditDiscount';
  });

  if (!discountField) {
    return null;
  }

  const discountAmount = Math.abs(discountField.Amount);
  const originalCharge = fields[0].AmountOrQuantity + discountAmount;

  return i18next.t('portfolio.transactions.details.insiderSavingsApplied', {
    discount: formatMoneyNumeral(discountAmount),
    originalCharge: formatMoneyNumeral(originalCharge),
  });
};

// options: see formatByTransactionSummary.
export function transactionFieldFormatter(field, options) {
  const formatType = parseEnumType(transactionFormatTypes, field.FormatType);
  switch (formatType) {
    case 'Product':
      return formatByProduct(field);

    case 'DateRange':
      return formatByDateRange(field);

    case 'TransactionSummary':
      return formatByTransactionSummary(field, options);

    default:
      break;
  }
}
